import {
  computed,
  inject,
  Injectable,
  resource,
  ResourceLoaderParams,
  ResourceRef,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import { CONFIGURATION_SETTING_KEYS, ITariffRelationVia } from '@web/dtos';
import { IApiResponse } from '@web/shared-types';
import { firstValueFrom } from 'rxjs';
import { ConfigurationStore } from '../../configuration/configuration-store.service';
import { IViaRequest, ViasGateway } from './vias.gateway';

@Injectable({
  providedIn: 'root',
})
export class ViasStore {
  private readonly configurationStore: ConfigurationStore =
    inject(ConfigurationStore);
  private readonly serviceAreaId = computed(() =>
    this.configurationStore.getNumberConfiguration(
      CONFIGURATION_SETTING_KEYS.ServiceAreaId
    )
  );
  private readonly relationSearchStopLimit = computed(() =>
    this.configurationStore.getNumberConfiguration(
      CONFIGURATION_SETTING_KEYS.RelationSearchStopLimit
    )
  );

  private readonly gateway: ViasGateway = new ViasGateway();

  private readonly request: Signal<IViaRequest> = computed(() => {
    return {
      serviceAreaId: this.serviceAreaId(),
      maxResults: this.relationSearchStopLimit(),
      origin: this.origin(),
      destination: this.destination(),
      name: this.viaName(),
    };
  });
  private readonly origin: WritableSignal<string> = signal('');
  private readonly destination: WritableSignal<string> = signal('');
  private readonly viaName: WritableSignal<string> = signal('');

  #viasResource: ResourceRef<IApiResponse<ITariffRelationVia[]> | undefined> =
    resource({
      params: this.request,
      loader: ({
        params,
      }: ResourceLoaderParams<IViaRequest>): Promise<
        IApiResponse<ITariffRelationVia[]>
      > => {
        return firstValueFrom(
          this.gateway.get({
            serviceAreaId: params.serviceAreaId,
            maxResults: params.maxResults,
            origin: params.origin,
            destination: params.destination,
            name: params.name,
          })
        );
      },
    });

  readonly isLoading: Signal<boolean> = computed((): boolean =>
    this.#viasResource.isLoading()
  );
  readonly vias: Signal<ITariffRelationVia[]> = computed(
    (): ITariffRelationVia[] => this.#viasResource.value()?.data ?? []
  );

  reload(): void {
    this.#viasResource.reload();
  }

  public loadVias(origin: string, destination: string, name: string): void {
    this.origin.set(origin);
    this.destination.set(destination);
    this.viaName.set(name);
  }
}
